import { createFileRoute } from "@tanstack/react-router";
import { PageHero } from "@/components/site/PageHero";
import { Section, SectionHeader } from "@/components/site/Section";
import { AdvisorCTA } from "@/components/site/AdvisorCTA";
import { SITE_URL } from "@/lib/seo";

export const Route = createFileRoute("/about-us")({
  head: () => ({
    links: [{ rel: "canonical", href: `${SITE_URL}/about-us/` }],
    meta: [
      { title: "About Leadership Edge Live" },
      {
        name: "description",
        content:
          "Most companies promote their best doers, then leave them to figure out leadership alone. We close that gap with live courses your team doesn't have to build.",
      },
      { property: "og:title", content: "About Leadership Edge Live" },
      {
        property: "og:description",
        content: "Live leadership courses for companies without a training department.",
      },
    ],
  }),
  component: About,
});

function About() {
  return (
    <>
      <PageHero
        maxWidth="6xl"
        headline={
          <>
            Most companies promote their best doers, then{" "}
            <span className="text-[#693D94]">leave them to figure out leadership alone.</span>
          </>
        }
        supporting="Your best technician becomes a supervisor on Monday. Same person, brand-new job, no instructions. We exist to close that gap before it costs you good people."
        ctas={[{ label: "See how it works", to: "/solutions/", variant: "primary" }]}
      />
      <Section tone="default">
        <SectionHeader eyebrow="How we think about it" title="What we believe" />

        <div className="grid gap-6 md:grid-cols-2">
          {[
            {
              title: "Leadership is behavior, not personality.",
              body: "We teach 16 leader behaviors across four dimensions. Behaviors can be named, practiced, and measured. Personality can't.",
            },
            {
              title: "Practice beats content.",
              body: "Watching a video changes nothing. Trying something at work on Tuesday and reporting back the next week changes a lot.",
            },
            {
              title: "Live beats self-paced.",
              body: "Every session is taught by a real facilitator with real people in the room. That's why people finish.",
            },
            {
              title: "You shouldn't need a training department.",
              body: "We handle registration, facilitation, participant support, and reporting. You pick the courses and read the results.",
            },
          ].map((b) => (
            <div key={b.title} className="rounded-2xl border border-border/60 bg-background p-7">
              <h3 className="font-display text-lg text-ink">{b.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{b.body}</p>
            </div>
          ))}
        </div>
      </Section>
      <Section tone="lavender">
        <div className="max-w-2xl">
          <h2 className="font-display text-3xl text-ink">What we're not</h2>
          <p className="mt-4 text-lg leading-relaxed text-muted-foreground">
            We're not a video library, and we're not a one-day workshop everyone forgets by Friday.
            Every session is live and led by a real facilitator.
          </p>
        </div>
      </Section>
      <AdvisorCTA
        title="Ready to start a focused conversation?"
        body="Tell us what's going on with your managers. We'll tell you if we can help."
      />
    </>
  );
}
